import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import {
  Stack,
  Typography,
  FormControl,
  Tooltip,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  ListSubheader,
  SelectChangeEvent,
  Button,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useState, useEffect, ReactElement } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import { groupBy } from 'lodash'
import { formatDate } from '../../shared/utils'
import { defaultLocale, fsColors, ReportsAvailable } from '../../shared/constants'
import { useAppSelector } from '../../store/hooks'
import { reportAPI } from '../../shared/api/ReportAPI'
import FsDemoAccountModal from '../common/molecules/DemoAccountModal'

const FsLegalMenu = ({
  onClose,
  setGenerationStatus,
}: {
  onClose: any
  setGenerationStatus: (status: string) => void
}) => {
  const [firstRunTime, setFirstRunTime] = useState<Date | undefined>(undefined)
  const [lastRunTime, setLastRunTime] = useState<Date | undefined>(undefined)
  const [startValue, setStartValue] = useState<Date | undefined>(undefined)
  const [endValue, setEndValue] = useState<Date | undefined>(undefined)
  const [periodValue, setPeriodValue] = useState<string>('3')

  const { t } = useTranslation()
  const { runs, currentRun } = useAppSelector((state) => state.currentAnalysis)
  const { currentLanguage, company, account } = useAppSelector((state) => state.currentUser)
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)
  const [currentReportId, setCurrentReportId] = useState<number | null>(1)

  useEffect(() => {
    if (runs) {
      const firstRun = runs[0]
      const lastRun = runs[runs.length - 1]

      setStartValue(new Date(firstRun.runAt))
      setEndValue(new Date(lastRun.runAt))

      setFirstRunTime(firstRun.runAt)
      setLastRunTime(lastRun.runAt)
    }
  }, [runs])

  const filteredRuns = runs ? runs?.filter((r) => r !== null || r !== undefined) : []

  const groupedDates = groupBy(filteredRuns.reverse(), (run) =>
    formatDate(run.runAt, currentLanguage, {
      year: 'numeric',
    }),
  )

  const groupedDateComponents: ReactElement[] = []
  Object.keys(groupedDates)
    .sort((a, b) => parseInt(b, 10) - parseInt(a, 10))
    .forEach((year, yindex) => {
      groupedDateComponents.push(<ListSubheader key={year}>{year}</ListSubheader>)
      groupedDates[year].forEach((run, index) => {
        groupedDateComponents.push(
          <MenuItem key={`${year}_${index}`} value={`${run.id}`}>{`${formatDate(
            run.runAt,
            currentLanguage,
          )}${index === 0 && yindex === 0 ? ` (${t('latest.date')})` : ''}`}</MenuItem>,
        )
      })
    })

  const closeDrawer = () => {
    setStartValue(firstRunTime)
    setEndValue(lastRunTime)
    setPeriodValue('3')
    setCurrentReportId(null)
    onClose()
  }

  const isDemoAccount = process.env.REACT_APP_SLUG === 'demo' && !account?.isSuperuser // getIsDemoAccount()

  const generateReport = async () => {
    if (isDemoAccount) {
      setOpenDemoModal(true)
      return
    }

    const currentReport = ReportsAvailable.find((r) => r.reportId === currentReportId)
    if (periodValue && endValue && currentRun && currentReport) {
      setGenerationStatus('loading')
      try {
        await reportAPI.generateReport(
          currentReport.reportModule,
          'legal',
          {
            colors: currentReport.theme,
            originalColors: currentReport.theme,
            shortName: company,
            name: company,
            primary: fsColors.fsDarkBlue.colors[0],
            secondary: fsColors.fsDarkBlue.colors[1],
            tertiary: fsColors.fsDarkBlue.colors[2],
            contrast: fsColors.fsOrange.colors[0],
          },
          endValue ?? currentRun.runAt,
          periodValue,
          'de',
        )
        setGenerationStatus('success')
      } catch (error: any) {
        setGenerationStatus('failed')
      }
    }
  }

  return (
    <Stack>
      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} />
      <Typography variant='h3' mb={1.5}>{t('law')}</Typography>
      <FormControl sx={{ width: 300, mb: 2 }} size="small">
        <Select
          onChange={(event) => {
            if (event.target.value) setCurrentReportId(Number(event.target.value))
          }}
          value={currentReportId}
        >
          {ReportsAvailable.map((report, index) => {
            if (report.isAvailable) {
              return (
                <MenuItem key={index} value={report.reportId}>
                  {report.name}
                </MenuItem>
              )
            }
            if (!report.isAvailable) {
              return (
                <span key={index}>
                  <Tooltip title={<Typography>{t('comingsoon')}</Typography>} followCursor>
                    <span>
                      <MenuItem style={{ pointerEvents: 'none' }} value={report.reportId} disabled>
                        {report.name}
                      </MenuItem>
                    </span>
                  </Tooltip>
                </span>
              )
            }
            return null
          })}
        </Select>
      </FormControl>
      <Typography variant='h3' mt={2} mb={1}>
        {t('reporting.year')}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={defaultLocale}>
        <Stack>
          <DatePicker
            sx={{ width: 300, mb: 2 }}
            disableFuture
            views={['year']}
            minDate={
              firstRunTime !== undefined && startValue !== undefined && startValue > firstRunTime
                ? firstRunTime
                : startValue
            }
            maxDate={lastRunTime}
            value={endValue}
            onChange={(newValue: any) => {
              if (newValue && startValue) {
                setEndValue(newValue)
              }
            }}
            slotProps={{
              field: {
                readOnly: true,
              },
              textField: {
                size: 'small',
              },
            }}
          />
        </Stack>
      </LocalizationProvider>
      <Typography variant='h3' className='mt-3 mb-2'>{t('duration')}</Typography>
      <Typography variant="body2" className='mb-2'>
        <InfoOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
        {t('report.duration.explain')}
      </Typography>
      <FormControl>
        <RadioGroup
          defaultValue="3"
          onChange={(event: SelectChangeEvent) => {
            setPeriodValue(event.target.value)
          }}
        >
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', mb: 3 }}>
            <FormControlLabel value="3" control={<Radio />} label={<span style={{ fontSize: '1rem' }}>{`3 ${t('years')} `}</span>} />
            <FormControlLabel value="5" control={<Radio />} label={<span style={{ fontSize: '1rem' }}>{`5 ${t('years')} `}</span>} />
          </Stack>
        </RadioGroup>
      </FormControl>
      <Typography variant='h3' className="mt-3 mb-4">{t('language')}</Typography>
      <FormControl>
        <RadioGroup defaultValue="de">
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', mb: 3 }}>
            <FormControlLabel value="de" control={<Radio />} label={<span style={{ fontSize: '1rem' }}>{t('german.label')}</span>} />
          </Stack>
        </RadioGroup>
      </FormControl>
      <Stack sx={{ alignItems: 'flex-end', mt: 2 }}>
        <Button
          disabled={!(periodValue && startValue && endValue)}
          variant="contained"
          onClick={generateReport}
        >
          <Typography>{t('get.report')}</Typography>
        </Button>
      </Stack>
    </Stack>
  )
}

export default FsLegalMenu
